import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Search,
  FilterList,
  Warning,
  Phone,
  MonitorHeart,
  Refresh,
  Emergency,
  Favorite,
  FitnessCenter,
  Group,
  AccessTime,
  Edit,
  TrendingUp,
  TrendingDown,
  TrendingFlat
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Patient, HealthMetric } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { getCacheBustedImageUrl } from '../../utils/s3Utils';

const API_URL = process.env.REACT_APP_API_URL;

export default function HealthMonitorPage() {
  const { user, isLoading } = useAuth();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [vitalFilter, setVitalFilter] = useState<'all' | 'heart_rate' | 'bp' | 'bmi'>('heart_rate');
  const [statusFilter, setStatusFilter] = useState<'all' | 'critical' | 'warning' | 'normal'>('all');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Pagination State
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);

  // Reusable function to transform API data
  const transformPatientData = useCallback((apiData: any[]): Patient[] => {
    return apiData.map((item: any, index: number) => {
      // Check if item has nested structure (patient + latest_vitals) or flat structure
      const patientInfo = item.patient || item;
      // New structure: latest_vitals has nested objects: bmi, bp, heartrate, steps
      const vitalsObj = item.latest_vitals || {};

      // Extract nested vital objects safely
      const bmiData = vitalsObj.bmi || {};
      const bpData = vitalsObj.bp || {};
      const heartData = vitalsObj.heartrate || vitalsObj.heart_rate || vitalsObj.heartRate || {}; // Added fallbacks
      const stepsData = vitalsObj.steps || {};
      // Merge potential sources: heartData, blood_sugar, top level vitals, and item/patientInfo (in case of flat structure)
      const bloodSugarData = {
        ...item,
        ...(patientInfo || {}),
        ...(vitalsObj || {}),
        ...(heartData || {}),
        ...(vitalsObj.blood_sugar || {})
      };



      // Extract Values from nested objects
      // Use NaN check instead of || 0 to differentiate between 0 and missing/invalid
      const parseTopLevelInt = (val: any) => {
        const parsed = parseInt(val);
        return isNaN(parsed) ? null : parsed;
      };
      const parseTopLevelFloat = (val: any) => {
        const parsed = parseFloat(val);
        return isNaN(parsed) ? null : parsed;
      };

      const heartRateVal = parseTopLevelInt(heartData.heart_rate);
      const systolicVal = parseTopLevelInt(bpData.systolic_bp);
      const diastolicVal = parseTopLevelInt(bpData.diastolic_bp);
      const bmiVal = parseTopLevelFloat(bmiData.bmi);
      const stepsVal = parseTopLevelInt(stepsData.steps);




      const heartRateId = heartData.id || heartData._id;
      const bpId = bpData.id || bpData._id;
      const bmiId = bmiData.id || bmiData._id;

      // Custom Status Calculation Logic
      const calculateHeartRateStatus = (bpm: number | null): string | null => {
        if (bpm === null) return null;
        if (bpm < 60) return 'Slow';
        if (bpm <= 85) return 'Normal';
        if (bpm <= 100) return 'Elevated';
        if (bpm <= 120) return 'High';
        return 'Very High';
      };

      const calculateBPStatus = (systolic: number | null, diastolic: number | null): string | null => {
        if (!systolic || !diastolic) return null;
        if (systolic > 180 || diastolic > 120) return 'Hypertensive Crisis';
        if (systolic >= 140 || diastolic >= 90) return 'Stage 2 Hypertension';
        if ((systolic >= 130 && systolic <= 139) || (diastolic >= 80 && diastolic <= 89)) return 'Stage 1 Hypertension';
        if ((systolic >= 120 && systolic <= 129) && diastolic < 80) return 'Elevated';
        return 'Normal';
      };

      const calculateBMIStatus = (bmi: number | null): string | null => {
        if (bmi === null) return null;
        if (bmi < 18.5) return 'Underweight';
        if (bmi <= 24.9) return 'Normal';
        if (bmi <= 29.9) return 'Overweight';
        return 'Obese';
      };

      // Map detailed status to Category
      const mapStatusToCategory = (status: string | null): 'critical' | 'warning' | 'normal' => {
        if (!status) return 'normal';
        const s = status.toLowerCase();

        // Critical
        if (['high', 'very high', 'stage 2 hypertension', 'hypertensive crisis', 'obese', 'critical'].includes(s)) return 'critical';

        // Warning
        if (['slow', 'elevated', 'stage 1 hypertension', 'underweight', 'overweight', 'warning'].includes(s)) return 'warning';

        return 'normal';
      };

      // Extract Statuses from nested objects
      const heartRateDetail = heartRateVal !== null ? calculateHeartRateStatus(heartRateVal) : (heartData.heart_rate_status || null);
      const bpDetail = (systolicVal && diastolicVal) ? calculateBPStatus(systolicVal, diastolicVal) : (bpData.bp_status || null);
      const bmiDetail = bmiVal !== null ? calculateBMIStatus(bmiVal) : (bmiData.bmi_status || null);

      const heartRateStatus = mapStatusToCategory(heartRateDetail);
      const bpStatus = mapStatusToCategory(bpDetail);
      const bmiStatus = mapStatusToCategory(bmiDetail);

      // Determine overall health status from vital statuses
      const getOverallStatus = (): 'critical' | 'warning' | 'normal' => {
        const statuses = [heartRateStatus, bpStatus, bmiStatus];
        if (statuses.includes('critical')) return 'critical';
        if (statuses.includes('warning')) return 'warning';
        return 'normal';
      };

      // Robust check for timestamp in various locations based on active filter
      const getTimestamp = () => {
        // 1. Prioritize based on vitalFilter if set
        if (vitalFilter === 'heart_rate') {
          if (heartData.heart_rate_created_date && heartData.heart_rate_created_time) {
            return `${heartData.heart_rate_created_date}T${heartData.heart_rate_created_time}`;
          }
          if (heartData.recorded_at) return heartData.recorded_at;
        }

        if (vitalFilter === 'bp') {
          if (bpData.bp_created_date && bpData.bp_created_time) {
            return `${bpData.bp_created_date}T${bpData.bp_created_time}`;
          }
          if (bpData.recorded_at) return bpData.recorded_at;
        }

        if (vitalFilter === 'bmi') {
          if (bmiData.bmi_created_date && bmiData.bmi_created_time) {
            return `${bmiData.bmi_created_date}T${bmiData.bmi_created_time}`;
          }
          if (bmiData.recorded_at) return bmiData.recorded_at;
        }

        // 2. Fallback / Default (e.g. 'all' filter or missing specific timestamp)
        // Try to find the most recent one or default to general recorded_at?
        // The original logic prioritized heart rate, which caused the bug.
        // Let's check generally for created_date fields first

        if (heartData.heart_rate_created_date && heartData.heart_rate_created_time) {
          // We only want to return this if we are sure it's the "primary" one, 
          // but "all" implies we show the latest interaction.
          // Ideally we would compare dates, but that's expensive inside this map if not needed.
          // For now, let's keep the fallback order but maybe prioritize the one that matches "lastHealthReading" values?
          // If we modified the logic to *only* show values for the filtered type, we wouldn't have this ambiguity.
          // But since we are here:
          return `${heartData.heart_rate_created_date}T${heartData.heart_rate_created_time}`;
        }

        return vitalsObj.recorded_at ||
          heartData.recorded_at ||
          bpData.recorded_at ||
          bmiData.recorded_at ||
          item.recorded_at ||
          new Date().toISOString();
      };

      // Determine robust ID to avoid duplicate keys
      // If item.id exists and is NOT the same as patientInfo.id, use it (it's likely a record ID).
      // Otherwise fallback to `${patientInfo.id || patientInfo._id}_${index}` to guarantee uniqueness.
      const rowId = (item.id && item.id !== (patientInfo.id || patientInfo._id))
        ? item.id
        : `${patientInfo.id || patientInfo._id}_${index}`;

      return {
        // Use the top-level item.id (record ID) if available to ensure uniqueness, 
        // otherwise fallback to patient ID.
        id: rowId,
        patient_id: patientInfo.patient_id,
        name: patientInfo.name,
        age: patientInfo.age,
        gender: patientInfo.gender,
        phone: patientInfo.phone || patientInfo.contact_number,
        email: patientInfo.email || '',
        medicalHistory: patientInfo.medical_history || '',
        allergies: patientInfo.allergies || '',
        doctorId: patientInfo.doctor_id,
        doctorName: patientInfo.doctor_name,
        emergencyContactName: patientInfo.emergency_contact_name || '',
        emergencyContactPhone: patientInfo.emergency_contact_phone || '',
        emergencyContactRelationship: patientInfo.emergency_contact_relationship || '',
        is_active: patientInfo.is_active !== undefined ? patientInfo.is_active : true,
        created_at: patientInfo.created_at || new Date().toISOString(),
        updated_at: patientInfo.updated_at || new Date().toISOString(),
        last_visit_date: patientInfo.last_visit_date,
        profile_image: patientInfo.profile_image,

        // Health Status
        healthStatus: getOverallStatus(),
        heart_rate_status: heartRateStatus,  // Category for list display (critical/warning/normal)
        bp_status: bpStatus,  // Category for list display (critical/warning/normal)
        bmi_status: bmiStatus,  // Category for list display (critical/warning/normal)

        // Detailed Status for popup display
        heart_rate_status_detail: heartRateDetail,  // Detailed status (Slow/Normal/Elevated/High/Very High)
        bp_status_detail: bpDetail,  // Detailed status (Normal/Elevated/Stage 1/Stage 2/Crisis)
        bmi_status_detail: bmiDetail,  // Detailed status (Underweight/Normal/Overweight/Obese)

        // Additional Heart Rate fields
        heart_rate_activity: heartData.heart_rate_activity || '',
        heart_rate_notes: heartData.heart_rate_notes || '',

        // Additional BP fields
        bp_notes: bpData.bp_notes || '',
        bp_record_id: bpData.id || '', // Store BP record ID for editing

        // Additional BMI fields - Robust extraction
        weight: (parseTopLevelFloat(bmiData.weight) || parseTopLevelFloat(vitalsObj.weight) || parseTopLevelFloat(patientInfo.weight)) ?? undefined,
        height: (parseTopLevelFloat(bmiData.height) || parseTopLevelFloat(vitalsObj.height) || parseTopLevelFloat(patientInfo.height)) ?? undefined,

        // Blood Sugar Fields
        blood_sugar_before_meal: parseTopLevelInt(bloodSugarData.blood_sugar_before_meal) ?? undefined,
        blood_sugar_after_meal: parseTopLevelInt(bloodSugarData.blood_sugar_after_meal) ?? undefined,
        blood_sugar_random: parseTopLevelInt(bloodSugarData.blood_sugar_random) ?? undefined,
        blood_sugar_mgdl: parseTopLevelInt(bloodSugarData.blood_sugar_mgdl) ?? undefined,
        hba1c_percentage: parseTopLevelFloat(bloodSugarData.hba1c_percentage) ?? undefined,
        diabetes_status: bloodSugarData.diabetes_status || '',

        // Vitals Data - Store null if not available to handle "N/A" display correctly
        lastHealthReading: {
          heartRate: heartRateVal !== null ? heartRateVal : 0,
          bloodPressure: {
            systolic: systolicVal !== null ? systolicVal : 0,
            diastolic: diastolicVal !== null ? diastolicVal : 0,
          },
          pulseRate: heartRateVal !== null ? heartRateVal : 0, // Fallback
          bmi: bmiVal !== null ? bmiVal : 0,
          steps: stepsVal !== null ? stepsVal : 0,
          timestamp: getTimestamp(),
        },
        heart_rate_id: heartRateId,
        bp_id: bpId,
        bmi_id: bmiId,
        trend_direction: item.trend_direction || vitalsObj.trend_direction || patientInfo.trend_direction || undefined,
      };
    });
  }, [vitalFilter]);

  const loadPatients = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');

      if (!token) {
        setError('Authentication token not found');
        return;
      }

      const limit = paginationModel.pageSize;
      const skip = paginationModel.page * paginationModel.pageSize;

      // Build query parameters with activity_status filter
      const params = new URLSearchParams({
        limit: limit.toString(),
        skip: skip.toString(),
      });

      // Map vitalFilter to activity_status API parameter
      if (vitalFilter !== 'all') {
        const activityStatusMap: Record<string, string> = {
          'heart_rate': 'heartrate',
          'bp': 'bp',
          'bmi': 'bmi'
        };
        const activityStatus = activityStatusMap[vitalFilter];
        if (activityStatus) {
          params.append('activity_status', activityStatus);
        }
      }

      // Use limit and skip for server-side pagination with activity_status filter
      const response = await fetch(`${API_URL}/health/dashboard?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch dashboard data');
      }

      const data = await response.json();
      console.log('Dashboard API Response:', data);

      // Handle the new response structure
      const patientsList = data.patients || data.patients_with_data || [];
      const total = data.total_patients || patientsList.length; // Fallback to list length if total not provided

      const transformedPatients = transformPatientData(patientsList);

      setPatients(transformedPatients);
      setRowCount(total);

    } catch (err: any) {
      console.error('Error loading patients:', err);
      setError(err.message || 'Failed to load patient data');
    } finally {
      setLoading(false);
    }
  }, [paginationModel.pageSize, paginationModel.page, vitalFilter, transformPatientData]);

  useEffect(() => {
    // Don't load patients until user is loaded
    if (isLoading || !user) return;

    loadPatients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isLoading, loadPatients]);

  // Auto-refresh disabled to reduce server load
  // Users can manually refresh by changing filters or pagination
  // If real-time updates are needed, consider WebSockets or longer intervals (60+ seconds)

  // Optimize filtering with useMemo
  const filteredPatients = useMemo(() => {
    let filtered = patients.filter(patient =>
      patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      patient.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Filter by statusFilter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(patient => {
        let status: 'critical' | 'warning' | 'normal' | undefined = patient.healthStatus;

        // Refine status based on vitalFilter if active
        // Actually healthStatus should already reflect overall, but if user is filtering by heart_rate,
        // they probably want to see status OF heart_rate.
        if (vitalFilter === 'heart_rate') {
          status = patient.heart_rate_status;
        } else if (vitalFilter === 'bp') {
          status = patient.bp_status;
        } else if (vitalFilter === 'bmi') {
          status = patient.bmi_status;
        }

        return status === statusFilter;
      });
    }

    // For superadmin role, filter out normal status patients - show only critical and warning
    if (user?.role === 'superadmin') {
      filtered = filtered.filter(patient => {
        // Get the status based on current vital filter
        let status: 'critical' | 'warning' | 'normal' | undefined = patient.healthStatus;
        if (vitalFilter === 'heart_rate') {
          status = patient.heart_rate_status;
        } else if (vitalFilter === 'bp') {
          status = patient.bp_status;
        } else if (vitalFilter === 'bmi') {
          status = patient.bmi_status;
        }

        // Only show critical and warning, hide normal
        return status === 'critical' || status === 'warning';
      });
    }

    // Filter Logic Removed to show ALL patients regardless of vital status
    // The vitalFilter state will instead control the VISIBLE COLUMNS via the columns prop

    if (vitalFilter !== 'all') {
      filtered = filtered.filter(patient => {
        switch (vitalFilter) {
          case 'heart_rate':
            return !!patient.heart_rate_status;
          case 'bp':
            return !!patient.bp_status;
          case 'bmi':
            return !!patient.bmi_status;
          default:
            return true;
        }
      });
    }



    return filtered;
  }, [patients, searchTerm, statusFilter, vitalFilter, user?.role]);

  const getHealthStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'critical': return { color: '#d84315', bgColor: '#ffccbc' }; // Deep orange/red-orange for "Needs Priority Review"
      case 'warning': return { color: '#f9a825', bgColor: '#fff9c4' }; // Yellow-amber for "Needs Review"
      case 'normal': return { color: '#2e7d32', bgColor: '#e8f5e9' };
      default: return { color: '#616161', bgColor: '#f5f5f5' };
    }
  };

  const getHealthStatusLabel = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'critical': return 'Needs Priority Review';
      case 'warning': return 'Needs Review';
      case 'normal': return 'Within Usual Range';
      default: return 'Unknown';
    }
  };

  const handlePatientClick = (patient: Patient) => {
    setSelectedPatient(patient);
    if (!patient.lastHealthReading) return;
    setDialogOpen(true);
  };

  const handleEditClick = (patient: Patient) => {
    setSelectedPatient(patient);
    // Initialize form data with Blood Pressure values only
    const formData: any = {
      systolic_bp: patient.lastHealthReading?.bloodPressure?.systolic || '',
      diastolic_bp: patient.lastHealthReading?.bloodPressure?.diastolic || '',
      bp_status: (patient as any).bp_status_detail || '',
      bp_notes: (patient as any).bp_notes || '',
      bp_record_id: (patient as any).bp_record_id || '' // Store BP record ID
    };

    setEditFormData(formData);
    setEditDialogOpen(true);
  };

  const handleEditFormChange = (field: string, value: string) => {
    setEditFormData((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveEdit = async () => {
    if (!selectedPatient) return;

    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');

      if (!token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }

      // Get the BP record ID
      const recordId = editFormData.bp_record_id;
      if (!recordId) {
        setError('Blood pressure record ID not found');
        setLoading(false);
        return;
      }

      // Call the update API
      const response = await fetch(`${API_URL}/health/blood-pressure/update/${recordId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'accept': 'application/json'
        },
        body: JSON.stringify({
          systolic_bp: editFormData.systolic_bp ? String(editFormData.systolic_bp) : undefined,
          diastolic_bp: editFormData.diastolic_bp ? String(editFormData.diastolic_bp) : undefined,
          bp_notes: editFormData.bp_notes || undefined
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update blood pressure');
      }

      const result = await response.json();
      console.log('Blood pressure updated successfully:', result);

      // Close the edit dialog
      setEditDialogOpen(false);
      setEditFormData({});

      // Refresh the patient data
      await loadPatients();

    } catch (err) {
      console.error('Error saving blood pressure:', err);
      setError(err instanceof Error ? err.message : 'Failed to save blood pressure');
    } finally {
      setLoading(false);
    }
  };

  const handleEmergencyCall = (patient: Patient) => {
    alert(`Emergency call initiated for ${patient.name}\nPhone: ${patient.phone}`);
  };

  const getStatusLabel = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'critical': return 'Needs Priority Review'; // Or 'Critical' if they prefer
      case 'warning': return 'Needs Review'; // Or 'Warning'
      case 'normal': return 'Within Usual Range'; // Updated from 'Within Usual Range'
      default: return status?.toUpperCase() || 'UNKNOWN';
    }
  };

  // Define columns dynamically based on vitalFilter
  const columns = useMemo(() => {
    const baseColumns: GridColDef[] = [
      {
        field: 'name',
        headerName: 'Patient Name',
        width: user?.role === 'superadmin' ? 300 : 200,
        renderCell: (params) => {
          try {
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar
                  src={getCacheBustedImageUrl(params.row.profile_image)}
                  sx={{ width: 32, height: 32, bgcolor: params.row.profile_image ? 'transparent' : 'primary.main' }}
                >
                  {!params.row.profile_image && (params.value?.charAt(0) || 'U')}
                </Avatar>
                <Box>
                  <Typography variant="body2">{params.value || 'Unknown'}</Typography>
                </Box>
              </Box>
            );
          } catch (error) {
            return <span>Unknown</span>;
          }
        }
      },
    ];

    const ageGenderColumns: GridColDef[] = [
      {
        field: 'age',
        headerName: 'Age',
        width: 80,
      },
      {
        field: 'gender',
        headerName: 'Gender',
        width: 100,
        valueFormatter: (value: any) => {
          if (!value) return '';
          return String(value).charAt(0).toUpperCase() + String(value).slice(1);
        }
      },
    ];

    const statusColumn: GridColDef = {
      field: 'healthStatus',
      headerName: 'Status',
      width: 200,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (value, row) => {
        if (vitalFilter === 'heart_rate') return row.heart_rate_status;
        if (vitalFilter === 'bp') return row.bp_status;
        if (vitalFilter === 'bmi') return row.bmi_status;
        return row.healthStatus;
      },
      renderCell: (params) => {
        try {
          const value = params.value || 'unknown';
          const statusConfig = getHealthStatusColor(value);
          const statusLabel = getHealthStatusLabel(value);
          return (
            <Chip
              label={statusLabel}
              size="small"
              icon={value === 'critical' ? <Warning /> : <MonitorHeart />}
              sx={{
                bgcolor: statusConfig.bgColor,
                color: statusConfig.color,
                fontWeight: 'bold',
                '& .MuiChip-icon': {
                  color: statusConfig.color
                }
              }}
            />
          );
        } catch (error) {
          return <Chip label="UNKNOWN" size="small" />;
        }
      }
    };

    const heartRateColumn: GridColDef = {
      field: 'heartRate',
      headerName: 'Heart Rate',
      width: 120,
      valueGetter: (value, row) => {
        return row?.lastHealthReading?.heartRate || 0;
      },
      renderCell: (params) => {
        try {
          const val = params.row?.lastHealthReading?.heartRate;
          return val && val > 0 ? `${val} bpm` : 'N/A';
        } catch (error) {
          return 'N/A';
        }
      }
    };


    const bpColumn: GridColDef = {
      field: 'bloodPressure',
      headerName: 'Blood Pressure',
      width: 150,
      valueGetter: (value, row) => {
        return row?.lastHealthReading?.bloodPressure;
      },
      renderCell: (params) => {
        try {
          const bp = params.row?.lastHealthReading?.bloodPressure;
          return bp && bp.systolic > 0 && bp.diastolic > 0
            ? `${bp.systolic}/${bp.diastolic} mmHg`
            : 'N/A';
        } catch (error) {
          return 'N/A';
        }
      }
    };

    const bmiColumn: GridColDef = {
      field: 'bmi',
      headerName: 'BMI',
      width: 100,
      valueGetter: (value, row) => {
        return row?.lastHealthReading?.bmi || 0;
      },
      renderCell: (params) => {
        try {
          const val = params.row?.lastHealthReading?.bmi;
          return val && val > 0 ? val.toFixed(1) : 'N/A';
        } catch (error) {
          return 'N/A';
        }
      }
    };

    const lastUpdateColumn: GridColDef = {
      field: 'lastUpdate',
      headerName: 'Inserted Date',
      width: 200,
      valueGetter: (value, row) => {
        return row?.lastHealthReading?.timestamp;
      },
      renderCell: (params) => {
        try {
          const val = params.row?.lastHealthReading?.timestamp;
          if (!val) return 'N/A';
          // Check if val is just a time string or a full date string
          if (val) {
            return new Date(val).toLocaleString([], {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              hour12: true
            });
          }
          return 'N/A';
        } catch (error) {
          return 'N/A';
        }
      }
    };

    const trendColumn: GridColDef = {
      field: 'trend_direction',
      headerName: '7-Day Trend',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const trendObj = params.row.trend_direction;
        if (!trendObj) return 'N/A';

        // Pick the right trend based on active vitalFilter
        let trendValue = '';
        if (vitalFilter === 'heart_rate') {
          trendValue = trendObj.heart_rate || trendObj.overall || '';
        } else if (vitalFilter === 'bp') {
          trendValue = trendObj.bp || trendObj.overall || '';
        } else if (vitalFilter === 'bmi') {
          trendValue = trendObj.bmi || trendObj.overall || '';
        } else {
          trendValue = trendObj.overall || '';
        }

        if (!trendValue) return 'N/A';

        // Parse the API string (e.g. "↑ Rising", "→ Stable", "↓ Falling")
        const lowerTrend = trendValue.toLowerCase();
        let icon = null;
        let label = trendValue.replace(/^[↑↓→]\s*/, ''); // Remove the arrow prefix for the label
        let color = '';

        if (lowerTrend.includes('rising')) {
          icon = <TrendingUp fontSize="small" />;
          color = 'warning.main';
        } else if (lowerTrend.includes('falling')) {
          icon = <TrendingDown fontSize="small" />;
          color = 'info.main';
        } else if (lowerTrend.includes('stable')) {
          icon = <TrendingFlat fontSize="small" />;
          color = 'success.main';
        } else {
          // Fallback: show the raw value
          return (
            <Typography variant="body2" sx={{ fontWeight: 500 }}>{trendValue}</Typography>
          );
        }

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, color: color, width: '100%' }}>
            {icon}
            <Typography variant="body2" sx={{ fontWeight: 500 }}>{label}</Typography>
          </Box>
        );
      }
    };

    const actionsColumn: GridColDef = {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => handlePatientClick(params.row)}
            >
              <MonitorHeart />
            </IconButton>
          </Tooltip>
          {user?.role === 'doctor' && vitalFilter === 'bp' && (
            <Tooltip title="Edit Blood Pressure">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleEditClick(params.row)}
              >
                <Edit />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )
    };

    // Filter Logic
    let cols = [...baseColumns];

    // Always include Age and Gender
    cols = [...cols, ...ageGenderColumns];

    if (user?.role === 'superadmin') {
      // Super Admin View: Status Only
      cols = [...cols, statusColumn, trendColumn, lastUpdateColumn, actionsColumn];
    } else {
      // Doctor View: All Vitals
      if (vitalFilter === 'all') {
        cols = [...cols, heartRateColumn, bpColumn, bmiColumn, statusColumn, trendColumn, lastUpdateColumn, actionsColumn];
      } else if (vitalFilter === 'heart_rate') {
        cols = [...cols, heartRateColumn, statusColumn, trendColumn, lastUpdateColumn, actionsColumn];
      } else if (vitalFilter === 'bp') {
        cols = [...cols, bpColumn, statusColumn, trendColumn, lastUpdateColumn, actionsColumn];
      } else if (vitalFilter === 'bmi') {
        cols = [...cols, bmiColumn, statusColumn, trendColumn, lastUpdateColumn, actionsColumn];
      }
    }

    return cols;
  }, [vitalFilter, user?.role]);

  const criticalCount = filteredPatients.filter(p => p.healthStatus === 'critical').length;
  const warningCount = filteredPatients.filter(p => p.healthStatus === 'warning').length;

  if (isLoading || !user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h4" gutterBottom>
        {user?.role === 'superadmin' ? 'Patient Status Summary' : 'Health Monitoring Dashboard'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {loading && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Loading patient data...
        </Alert>
      )}

      {criticalCount > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {(user?.role === 'superadmin' || user?.role === 'doctor') ? (
            <>You have {criticalCount} patient(s) that need priority review</>
          ) : (
            <><strong>Priority Review:</strong> {criticalCount} patient(s) require immediate medical attention!</>
          )}
        </Alert>
      )}


      {/* Vital Filter Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)'
          },
          gap: 3,
          mb: 4
        }}
      >
        <Card
          sx={{
            bgcolor: vitalFilter === 'heart_rate' ? '#ff6f00' : 'white', // Changed from error.main to orange
            borderColor: vitalFilter === 'heart_rate' ? '#ff6f00' : 'transparent',
            borderWidth: vitalFilter === 'heart_rate' ? 2 : 0,
            borderStyle: 'solid',
            cursor: 'pointer',
            transition: 'all 0.2s',
            color: vitalFilter === 'heart_rate' ? 'white' : 'text.primary'
          }}
          onClick={() => setVitalFilter('heart_rate')}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography color={vitalFilter === 'heart_rate' ? 'inherit' : 'textSecondary'} variant="body2" fontWeight="bold">
                  Health Metric
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="inherit">
                  Heart Rate
                </Typography>
              </Box>
              <MonitorHeart sx={{ fontSize: 40, opacity: 0.8, color: vitalFilter === 'heart_rate' ? 'white' : '#ff6f00' }} />
            </Box>
          </CardContent>
        </Card>

        <Card
          sx={{
            bgcolor: vitalFilter === 'bp' ? 'warning.main' : 'white',
            borderColor: vitalFilter === 'bp' ? 'warning.main' : 'transparent',
            borderWidth: vitalFilter === 'bp' ? 2 : 0,
            borderStyle: 'solid',
            cursor: 'pointer',
            transition: 'all 0.2s',
            color: vitalFilter === 'bp' ? 'white' : 'text.primary'
          }}
          onClick={() => setVitalFilter('bp')}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography color={vitalFilter === 'bp' ? 'inherit' : 'textSecondary'} variant="body2" fontWeight="bold">
                  Health Metric
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="inherit">
                  Blood Pressure
                </Typography>
              </Box>
              <Favorite sx={{ fontSize: 40, opacity: 0.8, color: vitalFilter === 'bp' ? 'white' : 'warning.main' }} />
            </Box>
          </CardContent>
        </Card>

        <Card
          sx={{
            bgcolor: vitalFilter === 'bmi' ? 'success.main' : 'white',
            borderColor: vitalFilter === 'bmi' ? 'success.main' : 'transparent',
            borderWidth: vitalFilter === 'bmi' ? 2 : 0,
            borderStyle: 'solid',
            cursor: 'pointer',
            transition: 'all 0.2s',
            color: vitalFilter === 'bmi' ? 'white' : 'text.primary'
          }}
          onClick={() => setVitalFilter('bmi')}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography color={vitalFilter === 'bmi' ? 'inherit' : 'textSecondary'} variant="body2" fontWeight="bold">
                  Health Metric
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="inherit">
                  BMI / Weight
                </Typography>
              </Box>
              <FitnessCenter sx={{ fontSize: 40, opacity: 0.8, color: vitalFilter === 'bmi' ? 'white' : 'success.main' }} />
            </Box>
          </CardContent>
        </Card>

        {/* Total Patients Card (Non-Interactive) */}
        <Card sx={{ bgcolor: 'white', color: 'text.primary' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography color="textSecondary" variant="body2" sx={{ opacity: 0.9 }}>
                  Total Patients
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {patients.length}
                </Typography>
              </Box>
              <Group color="primary" sx={{ fontSize: 40, opacity: 0.8 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Filters (Search & Reset) */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 0.8fr' },
            gap: 2,
            alignItems: 'center'
          }}>
            <TextField
              fullWidth
              placeholder="Search patients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value as any)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="critical">Needs Priority Review</MenuItem>
                <MenuItem value="warning">Needs Review</MenuItem>
                <MenuItem value="normal">Within Usual Range</MenuItem>
              </Select>
            </FormControl>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                // setVitalFilter('all');
              }}
              sx={{ height: '56px' }}
            >
              Reset
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Patients Data Grid */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {/* Show "Patient Status Summary" for both Super Admin and Doctor */}
            {(user?.role === 'superadmin' || user?.role === 'doctor')
              ? 'Patient Status Summary'
              : 'Real-time Patient Health Monitor'}
          </Typography>
          <DataGrid
            rows={filteredPatients || []}
            columns={columns}
            paginationMode="server"
            rowCount={rowCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50, 100]}
            checkboxSelection={false}
            disableRowSelectionOnClick
            autoHeight
            loading={loading}
            sx={{
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Patient Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedPatient && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                src={getCacheBustedImageUrl(selectedPatient.profile_image)}
                sx={{ width: 50, height: 50, bgcolor: selectedPatient.profile_image ? 'transparent' : 'primary.main' }}
              >
                {!selectedPatient.profile_image && selectedPatient.name && selectedPatient.name.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="h6">{selectedPatient.name}</Typography>
                <Chip
                  label={selectedPatient.healthStatus.toUpperCase()}
                  color={getHealthStatusColor(selectedPatient.healthStatus).color as any}
                  size="small"
                />
              </Box>
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedPatient && (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              gap: 2
            }}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Current Vital Signs
                </Typography>
                <List>
                  {/* Heart Rate Details */}
                  {(vitalFilter === 'all' || vitalFilter === 'heart_rate') && (
                    <>
                      <ListItem>
                        <ListItemText
                          primary="Heart Rate"
                          secondary={selectedPatient.lastHealthReading && selectedPatient.lastHealthReading.heartRate ?
                            `${selectedPatient.lastHealthReading.heartRate} bpm` : 'N/A'}
                        />
                      </ListItem>
                      {vitalFilter === 'heart_rate' && (
                        <>
                          <ListItem>
                            <ListItemText
                              primary="Activity"
                              secondary={(selectedPatient as any).heart_rate_activity || 'N/A'}
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemText
                              primary="Notes"
                              secondary={(selectedPatient as any).heart_rate_notes || 'N/A'}
                            />
                          </ListItem>
                        </>
                      )}
                      {/* Blood Sugar / Diabetes Section */}
                      <ListItem>
                        <Typography variant="subtitle2" color="primary" sx={{ mt: 1, mb: 0.5 }}>
                          Diabetes / Blood Sugar
                        </Typography>
                      </ListItem>
                      <ListItem>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, width: '100%' }}>
                          <ListItemText
                            primary="Blood Sugar"
                            secondary={(selectedPatient as any).blood_sugar_mgdl ? `${(selectedPatient as any).blood_sugar_mgdl} mg/dL` : 'N/A'}
                          />
                          <ListItemText
                            primary="Random Blood Sugar"
                            secondary={(selectedPatient as any).blood_sugar_random ? `${(selectedPatient as any).blood_sugar_random} mg/dL` : 'N/A'}
                          />
                          <ListItemText
                            primary="Pre Prandial"
                            secondary={(selectedPatient as any).blood_sugar_before_meal ? `${(selectedPatient as any).blood_sugar_before_meal} mg/dL` : 'N/A'}
                          />
                          <ListItemText
                            primary="Post Prandial"
                            secondary={(selectedPatient as any).blood_sugar_after_meal ? `${(selectedPatient as any).blood_sugar_after_meal} mg/dL` : 'N/A'}
                          />
                          <ListItemText
                            primary="HbA1c"
                            secondary={(selectedPatient as any).hba1c_percentage ? `${(selectedPatient as any).hba1c_percentage}%` : 'N/A'}
                          />
                        </Box>
                      </ListItem>
                      {/* <ListItem>
                        <ListItemText
                          primary="Diabetes Status"
                          secondary={(selectedPatient as any).diabetes_status || 'N/A'}
                        />
                      </ListItem> */}
                    </>
                  )}

                  {/* Blood Pressure Details */}
                  {(vitalFilter === 'all' || vitalFilter === 'bp') && (
                    <>
                      <ListItem>
                        <ListItemText
                          primary="Systolic BP"
                          secondary={selectedPatient.lastHealthReading && selectedPatient.lastHealthReading.bloodPressure.systolic ?
                            `${selectedPatient.lastHealthReading.bloodPressure.systolic} mmHg` : 'N/A'}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Diastolic BP"
                          secondary={selectedPatient.lastHealthReading && selectedPatient.lastHealthReading.bloodPressure.diastolic ?
                            `${selectedPatient.lastHealthReading.bloodPressure.diastolic} mmHg` : 'N/A'}
                        />
                      </ListItem>
                      {vitalFilter === 'bp' && (
                        <>
                          <ListItem>
                            <ListItemText
                              primary="Notes"
                              secondary={(selectedPatient as any).bp_notes || 'N/A'}
                            />
                          </ListItem>
                        </>
                      )}
                    </>
                  )}

                  {/* BMI Details */}
                  {(vitalFilter === 'all' || vitalFilter === 'bmi') && (
                    <>
                      <ListItem>
                        <ListItemText
                          primary="Weight"
                          secondary={(selectedPatient as any).weight ? `${(selectedPatient as any).weight} kg` : 'N/A'}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="Height"
                          secondary={(selectedPatient as any).height ? `${(selectedPatient as any).height} cm` : 'N/A'}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText
                          primary="BMI"
                          secondary={selectedPatient.lastHealthReading && selectedPatient.lastHealthReading.bmi ?
                            `${selectedPatient.lastHealthReading.bmi.toFixed(1)}` : 'N/A'}
                        />
                      </ListItem>
                    </>
                  )}
                </List>
              </Box>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Patient Information
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Age"
                      secondary={`${selectedPatient.age} years`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Gender"
                      secondary={selectedPatient.gender && String(selectedPatient.gender).trim() ? (String(selectedPatient.gender).charAt(0).toUpperCase() + String(selectedPatient.gender).slice(1)) : 'N/A'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Medical History"
                      secondary={selectedPatient.medicalHistory || 'None'}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Emergency Contact"
                      secondary={selectedPatient.emergencyContactName ?
                        `${selectedPatient.emergencyContactName} (${selectedPatient.emergencyContactRelationship})\n${selectedPatient.emergencyContactPhone}` : 'N/A'}
                    />
                  </ListItem>
                </List>
              </Box>
            </Box>
          )}

          {/* CDS Disclaimer */}
          <Box sx={{ mt: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #2196f3' }}>
            <Typography variant="body2" sx={{ display: 'block', fontStyle: 'italic', textAlign: 'center', color: '#1976d2' }}>
              <strong>Clinical Decision Support Disclaimer:</strong> Trend-based insights — not diagnostic. Clinical decision remains with doctor.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            Close
          </Button>
          {/* <Button variant="contained" startIcon={<Phone />}>
            Call Patient
          </Button>
          <Button variant="contained" color="error" startIcon={<Emergency />}>
            Emergency Alert
          </Button> */}
        </DialogActions>
      </Dialog>

      {/* Edit Blood Pressure Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit Blood Pressure - {selectedPatient?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {/* Blood Pressure Section */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Systolic BP (mmHg)"
                type="number"
                value={editFormData.systolic_bp || ''}
                onChange={(e) => handleEditFormChange('systolic_bp', e.target.value)}
                fullWidth
              />
              <TextField
                label="Diastolic BP (mmHg)"
                type="number"
                value={editFormData.diastolic_bp || ''}
                onChange={(e) => handleEditFormChange('diastolic_bp', e.target.value)}
                fullWidth
              />
            </Box>
            <TextField
              label="BP Status"
              value={editFormData.bp_status || ''}
              onChange={(e) => handleEditFormChange('bp_status', e.target.value)}
              fullWidth
              disabled
              helperText="Auto-calculated based on BP values"
            />
            <TextField
              label="Notes"
              multiline
              rows={3}
              value={editFormData.bp_notes || ''}
              onChange={(e) => handleEditFormChange('bp_notes', e.target.value)}
              fullWidth
              placeholder="Additional notes about blood pressure reading"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveEdit}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box >
  );
}